import { useState, useMemo, useEffect, useCallback } from 'react'
import {
  Box, Container, Alert, CircularProgress, Stack, Button, Typography, Paper,
  Chip, Table, TableBody, TableCell, TableHead, TableRow, Link,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useDatabase } from '@/hooks/useDatabase'
import KPICards from '@/components/KPICards'
import { ExcelImport } from '@/components/ExcelImport'
import { MonthlyNettoChart, TopSuppliersChart } from '@/components/Charts'
import { InvoiceRow } from '@/types'
import { calculateKPIs, groupByMonth, getTopSuppliers } from '@/utils/filterUtils'
import { formatCurrency } from '@/utils/formatters'
import {
  normalizeStatus, STATUS_LABELS, STATUS_COLORS, isOverdue, isDueSoon, daysUntilDue,
} from '@/utils/invoiceStatus'

export default function DashboardPage() {
  const navigate = useNavigate()
  const [uploadedInvoices, setUploadedInvoices] = useState<InvoiceRow[] | null>(null)

  const { getInvoices } = useDatabase()

  // Database integration state
  const [dbInvoices, setDbInvoices] = useState<InvoiceRow[]>([])
  const [isUsingDb, setIsUsingDb] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showTaxKpis, setShowTaxKpis] = useState(false)

  // Fetch from database on mount
  const loadFromDatabase = useCallback(async () => {
    setIsLoading(true)
    try {
      const result = await getInvoices({ limit: 10000 })
      // Map DatabaseInvoice to InvoiceRow
      const mapped = result.data.map(db => {
        // Parse DD.MM.YYYY or ISO format to Date object
        let dateInvoice: Date | null = null
        if (db.dateInvoice) {
          // Try DD.MM.YYYY format
          const dmyMatch = db.dateInvoice.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
          if (dmyMatch) {
            const [, day, month, year] = dmyMatch
            dateInvoice = new Date(Number.parseInt(year, 10), Number.parseInt(month, 10) - 1, Number.parseInt(day, 10))
          } else {
            // Try ISO format
            dateInvoice = new Date(db.dateInvoice)
          }
          if (Number.isNaN(dateInvoice.getTime())) dateInvoice = null
        }

        return {
          supplierName: db.supplierName,
          dateInvoice,
          brutto: db.brutto,
          netto: db.netto,
          vatAmount: db.vatAmount,
          taxFreeAmount: db.taxFreeAmount,
          taxableNetAmount: db.taxableNetAmount,
          rechnungsnummer: db.rechnungsnummer,
          comment: db.comment,
          status: db.status,
          dueDate: db.dueDate,
          documentType: db.documentType,
        }
      })
      setDbInvoices(mapped)
      setIsUsingDb(mapped.length > 0)
    } catch (err) {
      console.error('Failed to load from database:', err)
    } finally {
      setIsLoading(false)
    }
  }, [getInvoices])

  useEffect(() => {
    void loadFromDatabase()
  }, [loadFromDatabase])

  // Use database invoices if available, otherwise uploaded invoices
  const activeInvoices = isUsingDb ? dbInvoices : (uploadedInvoices || [])

  // Handle file upload
  const handleDataLoaded = (data: InvoiceRow[]) => {
    setUploadedInvoices(data)
    setIsUsingDb(false)
  }

  // Clear uploaded data
  const handleClearUpload = () => {
    setUploadedInvoices(null)
  }

  // KPIs + charts span all active invoices (filtering now lives on the Database page)
  const kpi = useMemo(() => calculateKPIs(activeInvoices), [activeInvoices])
  const monthlyData = useMemo(() => groupByMonth(activeInvoices), [activeInvoices])
  const topSuppliers = useMemo(() => getTopSuppliers(activeInvoices, 10), [activeInvoices])

  // Invoices that still need the reviewer's attention, most urgent first
  const attention = useMemo(() => {
    const rank = (inv: InvoiceRow) => {
      if (isOverdue(inv.dueDate, inv.status, inv.documentType)) return 0
      if (normalizeStatus(inv.status) === 'in_review') return 1
      return 2
    }
    return activeInvoices
      .filter((inv) =>
        isOverdue(inv.dueDate, inv.status, inv.documentType) ||
        isDueSoon(inv.dueDate, inv.status, inv.documentType) ||
        normalizeStatus(inv.status) === 'in_review'
      )
      .sort((a, b) => {
        const r = rank(a) - rank(b)
        if (r !== 0) return r
        return (daysUntilDue(a.dueDate) ?? 9999) - (daysUntilDue(b.dueDate) ?? 9999)
      })
  }, [activeInvoices])

  // Show loading state
  if (isLoading && !activeInvoices.length) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3 }}>
            Loading data...
          </Typography>
        </Box>
      </Container>
    )
  }

  // Show initial state with upload option
  if (!activeInvoices.length) {
    return (
      <Container maxWidth="md">
        <Stack spacing={3} sx={{ mt: 4 }}>
          <Typography variant="h4" component="h1">
            Import Data
          </Typography>

          <Alert severity="info">
            <Typography variant="body2">
              Upload your Excel file with invoice data. The file is processed in your browser and not uploaded to any server.
            </Typography>
          </Alert>

          <Paper sx={{ p: 3 }}>
            <ExcelImport onDataLoaded={handleDataLoaded} fileName="Invoices" />
          </Paper>

          <Button variant="outlined" onClick={loadFromDatabase} disabled={isLoading}>
            {isLoading ? 'Loading...' : 'Refresh from Database'}
          </Button>
        </Stack>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl">
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" component="h1">
            Dashboard
          </Typography>
          <Stack direction="row" spacing={2}>
            {uploadedInvoices && (
              <Button variant="outlined" onClick={handleClearUpload}>
                Different File
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadFromDatabase}
              disabled={isLoading}
            >
              Refresh
            </Button>
          </Stack>
        </Box>

        {/* Data source notice */}
        {isUsingDb && (
          <Alert severity="info">
            {dbInvoices.length} invoices from database.
          </Alert>
        )}

        {uploadedInvoices && (
          <Alert severity="success">
            {uploadedInvoices.length} invoices from uploaded file.
          </Alert>
        )}

        {/* Due-date reminders */}
        {(kpi.overdueCount > 0 || kpi.dueSoonCount > 0) && (
          <Alert severity={kpi.overdueCount > 0 ? 'warning' : 'info'}>
            {kpi.overdueCount > 0 && (
              <strong>{kpi.overdueCount} overdue ({formatCurrency(kpi.overdueAmount)})</strong>
            )}
            {kpi.overdueCount > 0 && kpi.dueSoonCount > 0 && ' · '}
            {kpi.dueSoonCount > 0 &&
              `${kpi.dueSoonCount} due within 7 days (${formatCurrency(kpi.dueSoonAmount)})`}
          </Alert>
        )}

        {/* KPI Cards */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button
              size="small"
              onClick={() => setShowTaxKpis((v) => !v)}
              startIcon={showTaxKpis ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {showTaxKpis ? 'Hide tax details' : 'Tax details'}
            </Button>
          </Box>
          <KPICards kpi={kpi} isLoading={isLoading} showTax={showTaxKpis} />
        </Box>

        {/* Charts Row */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 0' }, minWidth: 0 }}>
            <MonthlyNettoChart data={monthlyData} isLoading={isLoading} />
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 0' }, minWidth: 0 }}>
            <TopSuppliersChart data={topSuppliers} isLoading={isLoading} />
          </Box>
        </Box>

        {/* Needs attention */}
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="h6">Needs attention ({attention.length})</Typography>
            <Link component="button" variant="body2" onClick={() => navigate('/database')}>
              View all in Database →
            </Link>
          </Stack>
          {attention.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
              Nothing needs attention — no overdue, due-soon, or in-review invoices.
            </Typography>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Due</TableCell>
                    <TableCell align="right">Gross</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attention.slice(0, 8).map((inv, i) => {
                    const s = normalizeStatus(inv.status)
                    const overdue = isOverdue(inv.dueDate, inv.status, inv.documentType)
                    const soon = !overdue && isDueSoon(inv.dueDate, inv.status, inv.documentType)
                    const d = daysUntilDue(inv.dueDate)
                    const dueLabel = inv.dueDate
                      ? `${inv.dueDate}${d != null ? (d < 0 ? ` · ${-d}d overdue` : ` · in ${d}d`) : ''}`
                      : '—'
                    return (
                      <TableRow
                        key={`${inv.rechnungsnummer}-${i}`}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate('/database')}
                      >
                        <TableCell>
                          <Chip size="small" label={STATUS_LABELS[s]} color={STATUS_COLORS[s]} />
                        </TableCell>
                        <TableCell>{inv.supplierName}</TableCell>
                        <TableCell>{inv.rechnungsnummer}</TableCell>
                        <TableCell
                          sx={{
                            color: overdue ? 'error.main' : soon ? 'warning.main' : 'inherit',
                            fontWeight: overdue || soon ? 600 : 400,
                          }}
                        >
                          {dueLabel}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(inv.brutto)}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
              {attention.length > 8 && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                  +{attention.length - 8} more — see Database
                </Typography>
              )}
            </>
          )}
        </Paper>
      </Stack>
    </Container>
  )
}
